// Session Validation Service - Handles timing issues and automatic logout
import securityService from './securityService.js';

class SessionValidationService {
  constructor() {
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
    this.sessionCheckInterval = 5 * 60 * 1000; // 5 minutes
    this.isValidating = false;
  }

  // Main session validation with retry logic
  async validateSession(token, userEmail = null, retryCount = 0) {
    try {
      // Prevent concurrent validations
      if (this.isValidating && retryCount === 0) {
        return { valid: true, reason: 'validation_in_progress' };
      }
      
      this.isValidating = true;
      
      const result = await securityService.validateSessionIntegrity(token, userEmail);
      this.isValidating = false;
      
      if (result.valid) {
        return { valid: true, token: result.refreshedToken || token };
      }
      
      // Handle token generation delay scenario
      if (this.isTokenGenerationDelay(result.error) && retryCount < this.maxRetries) {
        console.log(`Session validation retry ${retryCount + 1}/${this.maxRetries}`);
        await this.delay(this.retryDelay * (retryCount + 1)); // Exponential backoff
        return this.validateSession(token, userEmail, retryCount + 1);
      }
      
      // Session is genuinely invalid
      return { valid: false, reason: result.error };
      
    } catch (error) {
      this.isValidating = false;
      
      // Network/temporary errors - retry
      if (this.isTemporaryError(error) && retryCount < this.maxRetries) {
        console.log(`Network error, retrying: ${error.message}`);
        await this.delay(this.retryDelay * (retryCount + 1));
        return this.validateSession(token, userEmail, retryCount + 1);
      }
      
      // Permanent error - session invalid
      return { valid: false, reason: error.message };
    }
  }

  // Check if error indicates token generation delay
  isTokenGenerationDelay(error) {
    const delayIndicators = [
      'token_not_ready',
      'generation_in_progress', 
      'temporary_unavailable',
      'processing_delay'
    ];
    return delayIndicators.some(indicator => 
      error && error.toLowerCase().includes(indicator)
    );
  }

  // Check if error is temporary/network related
  isTemporaryError(error) {
    const temporaryErrors = [
      'network error',
      'timeout',
      'connection refused',
      'service unavailable',
      'internal server error'
    ];
    return temporaryErrors.some(temp => 
      error.message && error.message.toLowerCase().includes(temp)
    );
  }

  // Validate session on page load/navigation
  async validateOnPageLoad() {
    const token = sessionStorage.getItem('session_token');
    const userEmail = sessionStorage.getItem('user_email');
    
    if (!token) {
      this.handleInvalidSession('no_token');
      return false;
    }

    const result = await this.validateSession(token, userEmail);
    
    if (!result.valid) {
      this.handleInvalidSession(result.reason);
      return false;
    }

    // Update token if refreshed
    if (result.token && result.token !== token) {
      sessionStorage.setItem('session_token', result.token);
    }

    return true;
  }

  // Handle invalid session - automatic logout
  handleInvalidSession(reason) {
    console.log(`Session invalid: ${reason}`);
    
    // Clear all session data
    this.clearSessionData();
    
    // Emit logout event for components to handle
    if (window.eventBus) {
      window.eventBus.emit('session_expired', { reason });
    }
    
    // Redirect to login with reason
    const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
    const loginUrl = `/login.html?redirect=${currentUrl}&reason=${reason}`;
    
    setTimeout(() => {
      window.location.href = loginUrl;
    }, 100);
  }

  // Clear all session data
  clearSessionData() {
    // Clear sessionStorage
    const keysToKeep = ['brand_slug', 'language']; // Keep brand context
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      if (!keysToKeep.includes(key)) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear localStorage (except brand data)
    const localKeys = Object.keys(localStorage);
    localKeys.forEach(key => {
      if (!keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Clear cookies
    const cookies = document.cookie.split(";");
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  }

  // Start periodic session validation
  startPeriodicValidation() {
    // Clear any existing interval
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
    }

    this.validationInterval = setInterval(async () => {
      const token = sessionStorage.getItem('session_token');
      const userEmail = sessionStorage.getItem('user_email');
      
      if (token) {
        const result = await this.validateSession(token, userEmail);
        if (!result.valid) {
          this.handleInvalidSession(result.reason);
        } else if (result.token && result.token !== token) {
          sessionStorage.setItem('session_token', result.token);
        }
      }
    }, this.sessionCheckInterval);
  }

  // Stop periodic validation
  stopPeriodicValidation() {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = null;
    }
  }

  // Utility delay function
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const sessionValidationService = new SessionValidationService();